import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

export default function PrimeFactorizationCalc() {
  const [n, setN] = useState(360);
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const N = Math.abs(Math.floor(+n) || 0);
    if (N < 2) return { primes: [], expr: '', tree: [], isPrime: N === 1 ? false : false };
    let x = N;
    const primes = [];
    const tree = []; // each step: [x, prime, x/prime]
    for (let d = 2; d * d <= x; d++) {
      while (x % d === 0) {
        primes.push(d);
        tree.push([x, d, x / d]);
        x = x / d;
      }
    }
    if (x > 1) {
      primes.push(x);
      tree.push([x, x, 1]);
    }
    // Group exponents
    const exp = {};
    primes.forEach(p => { exp[p] = (exp[p] || 0) + 1; });
    const expr = Object.entries(exp).map(([p, e]) => e > 1 ? `${p}^${e}` : p).join(' × ');
    const isPrime = primes.length === 1 && primes[0] === N;
    return { primes, expr, tree, isPrime, exp };
  }, [n]);

  return (
    <CalcShell look="cnet" title="Prime Factorization Calculator" subtitle="Find the prime factorization of a positive integer.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Positive integer</label><input type="number" min="2" value={n} onChange={e => setN(e.target.value)} data-testid="pf-n" /></div>
        </div>
        <div>
          {c.primes.length > 0 ? (
            <div className="result-panel">
              <div className="result-title">Prime factorization</div>
              <div className="result-value font-mono text-2xl" data-testid="pf-result">{n} = {c.primes.join(' × ')}</div>
              <div className="text-sm text-slate-600 mt-2">Exponent form: <b className="font-mono">{c.expr}</b></div>
              {c.isPrime && <div className="text-sm text-emerald-700 font-semibold mt-1">{n} is a prime number.</div>}
              <div className="text-xs text-slate-500 mt-2">Number of prime factors (with multiplicity): {c.primes.length}. Distinct primes: {Object.keys(c.exp).length}.</div>
            </div>
          ) : (
            <div className="text-red-600">Please enter an integer ≥ 2.</div>
          )}
        </div>
      </div>

      {c.tree.length > 0 && (
        <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <h3 className="font-bold text-slate-900 mb-3">Factor Tree</h3>
          <ul className="space-y-1 text-sm font-mono" data-testid="pf-tree">
            {c.tree.map(([parent, p, child], i) => (
              <li key={i} className="pl-4 border-l-2 border-blue-300">
                <span className="text-slate-800">{parent}</span>
                <span className="text-slate-500"> = </span>
                <b className="text-[#4c7b25]">{p}</b>
                <span className="text-slate-500"> × </span>
                <span className={child === 1 ? 'text-emerald-700 font-bold' : 'text-slate-800'}>{child}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="pf-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && c.primes.length > 0 && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="pf-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Start</b> with the number {n}.</li>
              <li><b>Divide by the smallest prime</b> (2, 3, 5, 7, 11, …) that divides evenly. Record the prime and continue with the quotient.</li>
              <li><b>Trace of divisions</b>:
                <ul className="ml-4 list-disc font-mono text-sm">
                  {c.tree.map(([parent, p, child], i) => <li key={i}>{parent} ÷ {p} = {child}</li>)}
                </ul>
              </li>
              <li><b>Stop</b> when the quotient becomes 1. All the recorded divisors are the prime factors.</li>
              <li><b>Prime factorization</b>: <b className="text-[#4c7b25]">{n} = {c.primes.join(' × ')}</b></li>
              <li><b>Exponent form</b>: <span className="font-mono">{c.expr}</span> — this is the canonical prime factorization.</li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
